import React, { useState } from "react";
import { Routes, Route, Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import Dashboard from "./Dashboard";
import JobBoard from "./JobBoard";
import Events from "./Events";
import Blog from "./Blog";
import Threads from "./Threads";
import ThreadDetail from "./ThreadDetail";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Menu, X } from "lucide-react";

const MainApp = ({ user, onLogout }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Debug: Log user info to console
  console.log("MainApp user:", user);
  console.log("is_admin:", user?.is_admin);

  const isActive = (path) => {
    if (path === "/app" && location.pathname === "/app") return true;
    if (path !== "/app" && location.pathname.startsWith(path)) return true;
    return false;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-teal-50">
      <nav className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50 border-b border-orange-100">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Link to="/app" className="text-xl md:text-2xl font-bold text-gray-900" style={{ fontFamily: 'Merriweather, serif' }}>
                CC Seattle Network
              </Link>
              
              {/* Desktop Navigation */}
              <div className="hidden md:flex space-x-1 ml-8">
                <Link to="/app">
                  <Button 
                    data-testid="nav-home"
                    variant={location.pathname === "/app" ? "default" : "ghost"}
                    className={location.pathname === "/app" ? "bg-orange-500 text-white" : ""}
                  >
                    Home
                  </Button>
                </Link>
                <Link to="/app/events">
                  <Button 
                    data-testid="nav-events"
                    variant={isActive("/app/events") ? "default" : "ghost"}
                    className={isActive("/app/events") ? "bg-orange-500 text-white" : ""}
                  >
                    Events
                  </Button>
                </Link>
                <Link to="/app/blog">
                  <Button 
                    data-testid="nav-blog"
                    variant={isActive("/app/blog") ? "default" : "ghost"}
                    className={isActive("/app/blog") ? "bg-orange-500 text-white" : ""}
                  >
                    Articles
                  </Button>
                </Link>
                <Link to="/app/threads">
                  <Button 
                    data-testid="nav-threads"
                    variant={isActive("/app/threads") ? "default" : "ghost"}
                    className={isActive("/app/threads") ? "bg-orange-500 text-white" : ""}
                  >
                    Discussions
                  </Button>
                </Link>
                <Link to="/app/jobs">
                  <Button 
                    data-testid="nav-jobs"
                    variant={isActive("/app/jobs") ? "default" : "ghost"}
                    className={isActive("/app/jobs") ? "bg-orange-500 text-white" : ""}
                  >
                    Jobs
                  </Button>
                </Link>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              {/* Mobile Menu Button */}
              <button
                className="md:hidden p-2 hover:bg-gray-100 rounded"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-button"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger data-testid="user-menu">
                  <div className="flex items-center space-x-2 cursor-pointer">
                    <Avatar>
                      <AvatarImage src={user.picture} />
                      <AvatarFallback>{user.name?.[0]?.toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <span className="hidden md:block text-sm font-medium">{user.name}</span>
                  </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem 
                    onClick={() => window.open('https://ccseattlecommunitycenter.com/', '_blank')}
                    data-testid="community-home-button"
                  >
                    Community Center Home
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={onLogout} data-testid="logout-button">
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 border-t border-orange-100 mt-2">
              <div className="flex flex-col space-y-2 pt-4">
                <Link to="/app" onClick={() => setMobileMenuOpen(false)}>
                  <Button 
                    data-testid="mobile-nav-home"
                    variant={location.pathname === "/app" ? "default" : "ghost"}
                    className={`w-full justify-start ${location.pathname === "/app" ? "bg-orange-500 text-white" : ""}`}
                  >
                    Home
                  </Button>
                </Link>
                <Link to="/app/events" onClick={() => setMobileMenuOpen(false)}>
                  <Button 
                    data-testid="mobile-nav-events"
                    variant={isActive("/app/events") ? "default" : "ghost"}
                    className={`w-full justify-start ${isActive("/app/events") ? "bg-orange-500 text-white" : ""}`}
                  >
                    Events
                  </Button>
                </Link>
                <Link to="/app/blog" onClick={() => setMobileMenuOpen(false)}>
                  <Button 
                    data-testid="mobile-nav-blog"
                    variant={isActive("/app/blog") ? "default" : "ghost"}
                    className={`w-full justify-start ${isActive("/app/blog") ? "bg-orange-500 text-white" : ""}`}
                  >
                    Articles
                  </Button>
                </Link>
                <Link to="/app/threads" onClick={() => setMobileMenuOpen(false)}>
                  <Button 
                    data-testid="mobile-nav-threads"
                    variant={isActive("/app/threads") ? "default" : "ghost"}
                    className={`w-full justify-start ${isActive("/app/threads") ? "bg-orange-500 text-white" : ""}`}
                  >
                    Discussions
                  </Button>
                </Link>
                <Link to="/app/jobs" onClick={() => setMobileMenuOpen(false)}>
                  <Button 
                    data-testid="mobile-nav-jobs"
                    variant={isActive("/app/jobs") ? "default" : "ghost"}
                    className={`w-full justify-start ${isActive("/app/jobs") ? "bg-orange-500 text-white" : ""}`}
                  >
                    Jobs
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      <main className="container mx-auto px-4 py-8">
        <Routes>
          <Route path="/" element={<Dashboard user={user} />} />
          <Route path="/jobs" element={<JobBoard user={user} />} />
          <Route path="/events" element={<Events user={user} />} />
          <Route path="/blog" element={<Blog user={user} />} />
          <Route path="/threads" element={<Threads user={user} />} />
          <Route path="/threads/:threadId" element={<ThreadDetail user={user} />} />
        </Routes>
      </main>
    </div>
  );
};

export default MainApp;